// components/BulkImportCustomers/BulkCustomerUploadModal.jsx
import React, { useState } from "react";
import { Box, Typography, LinearProgress, Collapse, Paper } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "components/MaterialSymbol/MaterialIcons";
import { Error as ErrorIcon } from "components/MaterialSymbol/MaterialIcons";
import BulkCustomerUploadForm from "./BulkCustomerUploadForm";
import useBulkCustomerUpload from "hooks/useBulkCustomerUpload";
import Button from "components/CustomButtons/Button";

export default function BulkCustomerUploadModal({ dealerId, onClose }) {
    const {
        isUploading,
        progress,
        showSuccess,
        result,
        errorMessage,
        handleUpload,
    } = useBulkCustomerUpload();

    const [showErrorList, setShowErrorList] = useState(false);

    // ---------------------------------------------------------
    // 1. INITIAL FORM
    // ---------------------------------------------------------
    if (!isUploading && !showSuccess && !errorMessage) {
        return (
            <BulkCustomerUploadForm
                onClose={onClose}
                onConfirm={(file) => handleUpload(file, dealerId)}
            />
        );
    }

    // ---------------------------------------------------------
    // 2. LOADING / PROGRESS
    // ---------------------------------------------------------
    if (isUploading) {
        return (
            <Box textAlign="center" py={2}>
                <Typography variant="h6" style={{ fontWeight: 500, marginBottom: 12 }}>
                    Uploading CSV...
                </Typography>

                <LinearProgress
                    variant="determinate"
                    value={progress}
                    style={{ height: 10, borderRadius: 5 }}
                />

                <Typography style={{ marginTop: 8, fontWeight: 500 }}>
                    {Math.floor(progress)}%
                </Typography>

                <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                    Please do not close or refresh the page.
                </Typography>
            </Box>
        );
    }

    // ---------------------------------------------------------
    // 3. ERROR (fatal)
    // ---------------------------------------------------------
    if (!isUploading && errorMessage) {
        return (
            <Box textAlign="center" py={2}>
                <ErrorIcon style={{ fontSize: 48, color: "#f44336" }} />
                <Typography variant="h6" style={{ fontWeight: 600, marginTop: 10 }} color="error">
                    Import Failed
                </Typography>
                <Typography
                    style={{
                        marginTop: 8,
                        marginBottom: 20,
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {errorMessage}
                </Typography>

                <Button color="rose" size="sm" onClick={onClose}>
                    Close
                </Button>
            </Box>
        );
    }

    // ---------------------------------------------------------
    // 4. SUCCESS SUMMARY
    // ---------------------------------------------------------
    if (showSuccess && result) {
        const { successful, failed } = result;

        return (
            <Box textAlign="center" py={1}>
                <CheckCircleIcon style={{ fontSize: 52, color: "#4caf50" }} />
                <Typography variant="h5" style={{ fontWeight: 700, marginTop: 10 }} color="primary">
                    Import Completed
                </Typography>

                <Box mt={3} mb={2} display="flex" justifyContent="center" gap="20px">
                    {/* IMPORTED */}
                    <Paper
                        elevation={2}
                        style={{
                            padding: "10px 18px",
                            textAlign: "center",
                            minWidth: 120,
                        }}
                    >
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            Imported
                        </Typography>
                        <Typography
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "#4caf50",
                            }}
                        >
                            {successful.length}
                        </Typography>
                    </Paper>

                    {/* FAILED */}
                    <Paper
                        elevation={2}
                        style={{
                            padding: "10px 18px",
                            textAlign: "center",
                            minWidth: 120,
                        }}
                    >
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            Failed
                        </Typography>
                        <Typography
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "#f44336",
                            }}
                        >
                            {failed.length}
                        </Typography>
                    </Paper>
                </Box>

                {/* SHOW ERROR LIST IF ANY */}
                {failed.length > 0 && (
                    <>
                        <Typography
                            variant="body2"
                            color="primary"
                            style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                            onClick={() => setShowErrorList((prev) => !prev)}
                        >
                            {showErrorList
                                ? "Hide error details"
                                : `View error details (${failed.length})`}
                        </Typography>

                        <Collapse in={showErrorList}>
                            <Box mt={2} maxHeight="200px" overflow="auto" textAlign="left">
                                {failed.map((f, idx) => (
                                    <Paper
                                        key={idx}
                                        style={{
                                            padding: "10px",
                                            backgroundColor: "#ffebee",
                                            borderLeft: "4px solid #d32f2f",
                                            marginBottom: 8,
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                fontWeight: 600,
                                                color: "#d32f2f",
                                            }}
                                        >
                                            Row {f.row}
                                        </Typography>
                                        <Typography>{f.error}</Typography>
                                    </Paper>
                                ))}
                            </Box>
                        </Collapse>
                    </>
                )}

                <Box mt={3}>
                    <Button color="success" size="sm" onClick={onClose} style={{ minWidth: 120 }}>
                        Finish and Close
                    </Button>
                </Box>
            </Box>
        );
    }

    return null;
}
